// pages/estoque/EntradaNotaPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FaCheck, FaEdit, FaFileImport, FaPlus, FaTimes, FaTrashAlt } from 'react-icons/fa';
import PageContainer from '../../components/geral/PageContainer';
import PageContent from '../../components/geral/PageContent';
import PageFooter from '../../components/geral/PageFooter';
import Input from '../../components/input/Input';
import Select from '../../components/input/Select';
import { useNota } from '../../contexts/vendas/NotaContext';
import { useNotaItem } from '../../contexts/vendas/NotaItemContext';
import { useNatureza } from '../../contexts/vendas/NaturezaContext';
import { useFornecedor } from '../../contexts/pessoas/FornecedorContext';
import { useProduto } from '../../contexts/produtos/ProdutoContext';
import { useUnMedida } from '../../contexts/produtos/UnMedidaContext';
import { useMessage } from '../../contexts/geral/MessageContext';
import { useYesNo } from '../../contexts/geral/YesNoContext';
import { Nota } from '../../models/vendas/Nota';
import { NotaItem } from '../../models/vendas/NotaItem';
import { formatCurrency, formatNumeric, normalizeDecimalInputBr, parseNumeric } from '../../utils/numberUtils';
import ProdutoCad from '../produtos/produtos/ProdutoCad';
import ExcluirEntradaModal from './ExcluirEntradaModal';
import './EntradaNotaPage.css';

const hoje = () => new Date().toISOString().slice(0, 10);

const criarNotaInicial = () => ({
  ...new Nota(),
  id: 0,
  fornecedorId: '',
  fornecedor: null,
  naturezaId: '',
  dataEmissao: hoje(),
  modeloNF: '55',
  serie: '',
  numDoc: '',
  chave: '',
  observacao: '',
  vDesc: '',
  vFrete: '',
  vOutro: '',
  notaItens: [],
});

const criarItemInicial = () => ({
  ...new NotaItem(),
  produtoId: '',
  produto: null,
  qtdCom: '',
  vUnCom: '',
  vProd: 0,
  xProd: '',
  unCom: 'UN',
});

const formatarCampoDecimal = (valor) => (
  valor === '' || valor === null || valor === undefined
    ? ''
    : formatNumeric(parseNumeric(valor), 2)
);

const EntradaNotaPage = () => {
  const navigate = useNavigate();
  const { notaId } = useParams();
  const [searchParams] = useSearchParams();
  const modoEdicao = Boolean(notaId);
  const retornoConsulta = searchParams.get('retorno') || '/consultaEntradas';
  const { addNota, obterEntrada, atualizarEntrada, excluirEntrada } = useNota();
  const { notaItens, setNotaItens } = useNotaItem();
  const { naturezas } = useNatureza();
  const { fornecedores, showFornecedorSearch, showFornecedorCad } = useFornecedor();
  const { produtos, showProdutoSearch } = useProduto();
  const { unMedidas } = useUnMedida();
  const { showMessage } = useMessage();
  const { showYesNo } = useYesNo();

  const [nota, setNota] = useState(criarNotaInicial());
  const [notaItem, setNotaItem] = useState(criarItemInicial());
  const [indiceEdicao, setIndiceEdicao] = useState(null);
  const [showCadastroProduto, setShowCadastroProduto] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(modoEdicao);
  const [entradaOriginal, setEntradaOriginal] = useState('');
  const [showExclusao, setShowExclusao] = useState(false);
  const [justificativaExclusao, setJustificativaExclusao] = useState('');
  const [erroExclusao, setErroExclusao] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const produtoInputRef = useRef(null);
  const quantidadeInputRef = useRef(null);

  const naturezasEntrada = useMemo(
    () => naturezas.filter((natureza) => Number(natureza.tipoMov) === 1),
    [naturezas]
  );

  useEffect(() => {
    if (!modoEdicao) setNotaItens([]);
  }, [setNotaItens, modoEdicao]);

  useEffect(() => {
    if (!modoEdicao) return;

    const carregarEntrada = async () => {
      setIsLoading(true);
      try {
        const entrada = await obterEntrada(notaId);
        if (!entrada.editavel) {
          showMessage('Esta entrada não está ativa e não pode ser editada.', 'warning');
          navigate(retornoConsulta, { replace: true });
          return;
        }
        const notaCarregada = {
          ...criarNotaInicial(),
          id: entrada.id,
          fornecedorId: entrada.fornecedorId,
          fornecedor: { id: entrada.fornecedorId, pessoa: { nome: entrada.fornecedorNome } },
          naturezaId: entrada.naturezaId,
          dataEmissao: entrada.dataEmissao ? entrada.dataEmissao.slice(0, 10) : '',
          modeloNF: entrada.modeloNF || '',
          serie: entrada.serie ?? '',
          numDoc: entrada.numeroDocumento ?? '',
          chave: entrada.chave || '',
          observacao: entrada.observacao || '',
          vDesc: formatNumeric(entrada.valorDesconto || 0, 2),
          vFrete: formatNumeric(entrada.valorFrete || 0, 2),
          vOutro: formatNumeric(entrada.outrosValores || 0, 2),
        };
        const itensCarregados = (entrada.itens || []).map((item, index) => ({
          ...new NotaItem(),
          id: item.id || 0,
          item: index + 1,
          produtoId: item.produtoId,
          produto: { id: item.produtoId, descricao: item.produtoDescricao, unTrib: item.unidade, movEstoque: true },
          xProd: item.produtoDescricao,
          cfop: item.cfop,
          unCom: item.unidade || 'UN',
          qtdCom: item.quantidade,
          vUnCom: item.valorUnitario,
          vProd: item.valorTotal,
        }));
        setNota(notaCarregada);
        setNotaItens(itensCarregados);
        setEntradaOriginal(JSON.stringify({ nota: notaCarregada, itens: itensCarregados }));
      } catch (error) {
        showMessage(error?.message || 'Não foi possível carregar a entrada.', 'danger');
      } finally {
        setIsLoading(false);
      }
    };

    carregarEntrada();
  }, [notaId]);

  useEffect(() => {
    if (!nota.naturezaId && naturezasEntrada.length > 0) {
      setNota((atual) => ({ ...atual, naturezaId: naturezasEntrada[0].id }));
    }
  }, [naturezasEntrada, nota.naturezaId]);

  const subtotal = useMemo(
    () => notaItens.reduce((total, item) => total + parseNumeric(item.vProd), 0),
    [notaItens]
  );
  const totalNota = subtotal
    - parseNumeric(nota.vDesc)
    + parseNumeric(nota.vFrete)
    + parseNumeric(nota.vOutro);
  const totalItem = parseNumeric(notaItem.qtdCom) * parseNumeric(notaItem.vUnCom);
  const naturezaSelecionada = naturezasEntrada.find(
    (natureza) => String(natureza.id) === String(nota.naturezaId)
  );
  const unidadesMedidaOptions = useMemo(() => {
    const unidades = (unMedidas || [])
      .filter((unidade) => unidade?.sigla)
      .map((unidade) => ({
        value: unidade.sigla,
        label: unidade.descricao
          ? `${unidade.sigla} - ${unidade.descricao}`
          : unidade.sigla,
      }));
    const unidadeAtual = notaItem.unCom?.trim();
    if (unidadeAtual && !unidades.some(
      (unidade) => unidade.value.toUpperCase() === unidadeAtual.toUpperCase()
    )) {
      unidades.unshift({ value: unidadeAtual, label: unidadeAtual });
    }
    return [
      { value: '', label: unidades.length ? 'Selecione' : 'Nenhuma unidade cadastrada' },
      ...unidades,
    ];
  }, [unMedidas, notaItem.unCom]);
  const possuiAlteracoes = modoEdicao
    && Boolean(entradaOriginal)
    && JSON.stringify({ nota, itens: notaItens }) !== entradaOriginal;

  const limparFormularioItem = () => {
    setNotaItem(criarItemInicial());
    setIndiceEdicao(null);
    setTimeout(() => produtoInputRef.current?.focus(), 0);
  };

  const limparEntrada = () => {
    const novaNota = criarNotaInicial();
    novaNota.naturezaId = naturezasEntrada[0]?.id || '';
    setNota(novaNota);
    setNotaItens([]);
    limparFormularioItem();
  };

  const handleBuscarFornecedor = async () => {
    const selecionado = await showFornecedorSearch();
    if (selecionado) {
      setNota((atual) => ({ ...atual, fornecedorId: selecionado.id, fornecedor: selecionado }));
    }
  };

  const handleCadastrarFornecedor = async () => {
    const salvo = await showFornecedorCad();
    if (salvo) {
      setNota((atual) => ({ ...atual, fornecedorId: salvo.id, fornecedor: salvo }));
    }
  };

  const handleExitFornecedor = () => {
    if (!nota.fornecedorId) {
      setNota((atual) => ({ ...atual, fornecedor: null }));
      return;
    }
    const selecionado = fornecedores.find((item) => String(item.id) === String(nota.fornecedorId));
    if (!selecionado) {
      showMessage('Fornecedor não encontrado.', 'warning');
      setNota((atual) => ({ ...atual, fornecedorId: '', fornecedor: null }));
      return;
    }
    setNota((atual) => ({ ...atual, fornecedorId: selecionado.id, fornecedor: selecionado }));
  };

  const selecionarProduto = (produto) => {
    if (!produto) return;
    setNotaItem((atual) => ({
      ...atual,
      produtoId: produto.id,
      produto,
      xProd: produto.descricao,
      unCom: produto.unTrib || 'UN',
      vUnCom: formatNumeric(parseNumeric(atual.vUnCom || produto.custoUltimo || produto.preco || 0), 2),
    }));
    setTimeout(() => quantidadeInputRef.current?.focus(), 0);
  };

  const handleBuscarProduto = async () => selecionarProduto(await showProdutoSearch());

  const handleExitProduto = () => {
    if (!notaItem.produtoId) {
      setNotaItem((atual) => ({ ...atual, produto: null, xProd: '' }));
      return;
    }
    const produto = produtos.find((item) => String(item.id) === String(notaItem.produtoId));
    if (!produto) {
      showMessage('Produto não encontrado.', 'warning');
      setNotaItem((atual) => ({ ...atual, produtoId: '', produto: null, xProd: '' }));
      return;
    }
    selecionarProduto(produto);
  };

  const salvarItem = () => {
    const produtoId = Number(notaItem.produtoId);
    const quantidade = parseNumeric(notaItem.qtdCom);
    const valorUnitario = parseNumeric(notaItem.vUnCom);
    const produto = notaItem.produto || produtos.find((item) => item.id === produtoId);

    if (!produtoId || !produto) return showMessage('Selecione um produto para incluir.', 'warning');
    if (produto.movEstoque === false) return showMessage(`O produto ${produto.descricao} não movimenta estoque.`, 'warning');
    if (quantidade <= 0) return showMessage('A quantidade deve ser maior que zero.', 'warning');
    if (valorUnitario < 0) return showMessage('O valor unitário não pode ser negativo.', 'warning');

    const itemSalvo = {
      ...notaItem,
      id: notaItem.id || 0,
      produtoId,
      produto,
      xProd: produto.descricao,
      qtdCom: quantidade,
      vUnCom: valorUnitario,
      vProd: quantidade * valorUnitario,
      cfop: naturezaSelecionada?.cfopCod || notaItem.cfop || '1102',
      unCom: notaItem.unCom || produto.unTrib || 'UN',
    };

    setNotaItens((atuais) => {
      const novos = [...atuais];
      if (indiceEdicao === null) novos.push(itemSalvo);
      else novos[indiceEdicao] = itemSalvo;
      return novos.map((item, index) => ({ ...item, item: index + 1 }));
    });
    limparFormularioItem();
  };

  const editarItem = (index) => {
    const item = notaItens[index];
    setIndiceEdicao(index);
    setNotaItem({
      ...item,
      qtdCom: formatNumeric(parseNumeric(item.qtdCom), 2),
      vUnCom: formatNumeric(parseNumeric(item.vUnCom), 2),
    });
    setTimeout(() => quantidadeInputRef.current?.focus(), 0);
  };

  const excluirItem = async (index) => {
    if (!(await showYesNo(`Deseja remover o item ${index + 1} da entrada?`))) return;
    setNotaItens((atuais) => atuais
      .filter((_, itemIndex) => itemIndex !== index)
      .map((item, itemIndex) => ({ ...item, item: itemIndex + 1 })));
    if (indiceEdicao === index) limparFormularioItem();
  };

  const alterarDecimalNota = (campo, valor) => {
    setNota((atual) => ({ ...atual, [campo]: normalizeDecimalInputBr(valor, 2).replace(/-/g, '') }));
  };

  const buildPayload = () => ({
    fornecedorId: Number(nota.fornecedorId),
    naturezaId: Number(nota.naturezaId),
    tipoMov: 1,
    modeloNF: nota.modeloNF || null,
    serie: nota.serie === '' ? null : Number(nota.serie),
    numDoc: nota.numDoc === '' ? null : Number(nota.numDoc),
    chave: nota.chave?.replace(/\D/g, '') || null,
    observacao: nota.observacao || null,
    cfop: naturezaSelecionada?.cfopCod || '1102',
    vProd: subtotal,
    vDesc: parseNumeric(nota.vDesc),
    vFrete: parseNumeric(nota.vFrete),
    vOutro: parseNumeric(nota.vOutro),
    vNota: totalNota,
    dataEmissao: nota.dataEmissao ? `${nota.dataEmissao}T12:00:00` : null,
    notaItens: notaItens.map((item, index) => ({
      id: item.id || 0,
      item: index + 1,
      produtoId: Number(item.produtoId),
      cfop: item.cfop || naturezaSelecionada?.cfopCod || '1102',
      unCom: item.unCom || 'UN',
      qtdCom: parseNumeric(item.qtdCom),
      vUnCom: parseNumeric(item.vUnCom),
      vProd: parseNumeric(item.vProd),
      xProd: item.xProd || item.produto?.descricao || '',
    })),
  });

  const buildPayloadEdicao = () => ({
    fornecedorId: Number(nota.fornecedorId),
    naturezaId: Number(nota.naturezaId),
    dataEmissao: nota.dataEmissao ? `${nota.dataEmissao}T12:00:00` : null,
    modeloNF: nota.modeloNF || null,
    serie: nota.serie === '' ? null : Number(nota.serie),
    numeroDocumento: nota.numDoc === '' ? null : Number(nota.numDoc),
    chave: nota.chave?.replace(/\D/g, '') || null,
    observacao: nota.observacao || null,
    valorDesconto: parseNumeric(nota.vDesc),
    valorFrete: parseNumeric(nota.vFrete),
    outrosValores: parseNumeric(nota.vOutro),
    itens: notaItens.map((item) => ({
      produtoId: Number(item.produtoId),
      cfop: item.cfop || naturezaSelecionada?.cfopCod || '1102',
      unidade: item.unCom || 'UN',
      quantidade: parseNumeric(item.qtdCom),
      valorUnitario: parseNumeric(item.vUnCom),
      descricao: item.xProd || item.produto?.descricao || '',
    })),
  });

  const concluirEntrada = async () => {
    if (isSaving) return;
    if (!naturezasEntrada.length) return showMessage('Cadastre uma natureza de entrada antes de continuar.', 'warning');
    if (!nota.naturezaId) return showMessage('Selecione a natureza da entrada.', 'warning');
    if (!nota.fornecedorId) return showMessage('Selecione um fornecedor.', 'warning');
    if (!notaItens.length) return showMessage('Inclua ao menos um item na entrada.', 'warning');
    const chave = nota.chave?.replace(/\D/g, '') || '';
    if (chave && chave.length !== 44) return showMessage('A chave da NF-e deve possuir 44 dígitos.', 'warning');

    setIsSaving(true);
    try {
      if (modoEdicao) {
        const salva = await atualizarEntrada(notaId, buildPayloadEdicao());
        showMessage(`Entrada ${salva?.id || notaId} atualizada com sucesso.`, 'success');
        navigate(retornoConsulta);
      } else {
        const salva = await addNota(buildPayload());
        showMessage(`Entrada ${salva?.id || ''} concluída com sucesso.`, 'success');
        limparEntrada();
      }
    } catch (error) {
      showMessage(error?.message || 'Não foi possível concluir a entrada.', 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmarLimpeza = async () => {
    if (modoEdicao) {
      const atual = JSON.stringify({ nota, itens: notaItens });
      if (atual !== entradaOriginal && !(await showYesNo('Deseja cancelar a edição e descartar as alterações?'))) return;
      navigate(retornoConsulta);
      return;
    }
    const possuiDados = notaItens.length > 0 || nota.fornecedorId || nota.numDoc || nota.chave;
    if (possuiDados && !(await showYesNo('Deseja descartar os dados desta entrada?'))) return;
    limparEntrada();
  };

  const abrirExclusao = () => {
    setJustificativaExclusao('');
    setErroExclusao('');
    setShowExclusao(true);
  };

  const confirmarExclusao = async () => {
    if (isDeleting || justificativaExclusao.trim().length < 15) return;
    setIsDeleting(true);
    setErroExclusao('');
    try {
      await excluirEntrada(notaId, justificativaExclusao.trim());
      setShowExclusao(false);
      showMessage(`Entrada ${notaId} excluída e estoque estornado com sucesso.`, 'success');
      navigate(retornoConsulta);
    } catch (error) {
      setErroExclusao(error?.message || 'Não foi possível excluir a entrada.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <PageContainer><div className="entrada-nota-loading"><div className="spinner-border text-primary" /><strong>Carregando entrada #{notaId}...</strong></div></PageContainer>;
  }

  return (
    <PageContainer>
      <div className="entrada-nota-page">
        <header className="entrada-nota-cabecalho">
          <div>
            <h2 className="page-title">{modoEdicao ? `Editar entrada #${notaId}` : 'Entrada de mercadorias'}</h2>
            <span>{modoEdicao ? 'Revise o documento e ajuste os itens antes de salvar.' : 'Lance manualmente uma compra, devolução ou ajuste de estoque.'}</span>
          </div>
          {modoEdicao
            ? <button type="button" className="btn btn-outline-primary" onClick={confirmarLimpeza}>Voltar à consulta</button>
            : <button type="button" className="btn btn-outline-primary" onClick={() => navigate('/importarXML')}><FaFileImport /> Importar XML</button>}
        </header>

        <PageContent>
          <section className="entrada-nota-card">
            <div className="entrada-nota-card-title">
              <strong>Dados da entrada</strong>
              <span>Informe a operação e a identificação do documento.</span>
            </div>
            <div className="entrada-nota-grid entrada-nota-grid-documento">
              <Select
                label="Natureza da operação *"
                value={nota.naturezaId}
                size="100%"
                options={[
                  { value: '', label: naturezasEntrada.length ? 'Selecione' : 'Nenhuma natureza de entrada' },
                  ...naturezasEntrada.map((item) => ({ value: item.id, label: `${item.id} - ${item.descricao}` })),
                ]}
                onChange={(e) => setNota((atual) => ({ ...atual, naturezaId: e.target.value }))}
              />
              <Input label="Emissão" type="date" size="100%" value={nota.dataEmissao} onChange={(e) => setNota((atual) => ({ ...atual, dataEmissao: e.target.value }))} />
              <Select label="Modelo" size="100%" value={nota.modeloNF} options={[{ value: '', label: 'Sem modelo' }, { value: '55', label: '55 - NF-e' }, { value: '65', label: '65 - NFC-e' }]} onChange={(e) => setNota((atual) => ({ ...atual, modeloNF: e.target.value }))} />
              <Input label="Série" type="number" size="100%" value={nota.serie} onChange={(e) => setNota((atual) => ({ ...atual, serie: e.target.value }))} />
              <Input label="Número" type="number" size="100%" value={nota.numDoc} onChange={(e) => setNota((atual) => ({ ...atual, numDoc: e.target.value }))} />
            </div>
            <div className="entrada-nota-fornecedor">
              <Input label="Fornecedor *" size={80} value={nota.fornecedorId} onChange={(e) => setNota((atual) => ({ ...atual, fornecedorId: e.target.value }))} onBlur={handleExitFornecedor} btnBuscar={handleBuscarFornecedor} btnAdd={handleCadastrarFornecedor} />
              <Input label="Nome do fornecedor" size="100%" value={nota.fornecedor?.pessoa?.nome || ''} disabled />
              <Input label="Chave da NF-e" size="100%" maxLength={44} value={nota.chave} onChange={(e) => setNota((atual) => ({ ...atual, chave: e.target.value.replace(/\D/g, '') }))} />
            </div>
            <div className="entrada-nota-grid entrada-nota-grid-valores">
              <Input label="Desconto" size="100%" value={nota.vDesc} onChange={(e) => alterarDecimalNota('vDesc', e.target.value)} onBlur={() => setNota((atual) => ({ ...atual, vDesc: formatarCampoDecimal(atual.vDesc) }))} />
              <Input label="Frete" size="100%" value={nota.vFrete} onChange={(e) => alterarDecimalNota('vFrete', e.target.value)} onBlur={() => setNota((atual) => ({ ...atual, vFrete: formatarCampoDecimal(atual.vFrete) }))} />
              <Input label="Outros valores" size="100%" value={nota.vOutro} onChange={(e) => alterarDecimalNota('vOutro', e.target.value)} onBlur={() => setNota((atual) => ({ ...atual, vOutro: formatarCampoDecimal(atual.vOutro) }))} />
              <Input label="Observação" size="100%" value={nota.observacao} onChange={(e) => setNota((atual) => ({ ...atual, observacao: e.target.value }))} />
            </div>
          </section>

          <section className={`entrada-nota-card entrada-nota-item-card ${indiceEdicao !== null ? 'is-editing' : ''}`}>
            <div className="entrada-nota-card-title">
              <strong>{indiceEdicao === null ? 'Adicionar item' : `Editando item ${indiceEdicao + 1}`}</strong>
              <span>Use F2 para pesquisar ou Enter no valor unitário para confirmar.</span>
            </div>
            <div className="entrada-nota-item-grid">
              <Input inputRef={produtoInputRef} label="Produto *" size={80} value={notaItem.produtoId} onChange={(e) => setNotaItem((atual) => ({ ...atual, produtoId: e.target.value }))} onBlur={handleExitProduto} btnBuscar={handleBuscarProduto} btnAdd={() => setShowCadastroProduto(true)} />
              <Input label="Descrição" size="100%" value={notaItem.produto?.descricao || notaItem.xProd || ''} disabled />
              <Select label="Unidade" size="100%" style={{ minWidth: '200px' }} value={notaItem.unCom} options={unidadesMedidaOptions} onChange={(e) => setNotaItem((atual) => ({ ...atual, unCom: e.target.value }))} />
              <Input inputRef={quantidadeInputRef} label="Quantidade *" size="100%" value={notaItem.qtdCom} onChange={(e) => setNotaItem((atual) => ({ ...atual, qtdCom: normalizeDecimalInputBr(e.target.value, 3) }))} onBlur={() => setNotaItem((atual) => ({ ...atual, qtdCom: formatarCampoDecimal(atual.qtdCom) }))} />
              <Input label="Valor unitário *" size="100%" value={notaItem.vUnCom} onChange={(e) => setNotaItem((atual) => ({ ...atual, vUnCom: normalizeDecimalInputBr(e.target.value, 4) }))} onBlur={() => setNotaItem((atual) => ({ ...atual, vUnCom: formatarCampoDecimal(atual.vUnCom) }))} onKeyDown={(e) => e.key === 'Enter' && salvarItem()} />
              <div className="entrada-nota-total-item"><span>Total do item</span><strong>{formatCurrency(totalItem)}</strong></div>
              <div className="entrada-nota-item-actions">
                {indiceEdicao !== null && <button type="button" className="btn btn-outline-secondary" onClick={limparFormularioItem}><FaTimes /> Cancelar</button>}
                <button type="button" className="btn btn-success" onClick={salvarItem}>{indiceEdicao === null ? <FaPlus /> : <FaCheck />}{indiceEdicao === null ? 'Adicionar' : 'Atualizar'}</button>
              </div>
            </div>
          </section>

          <section className="entrada-nota-tabela-card">
            <div className="entrada-nota-table-header"><strong>Itens da entrada</strong><span>{notaItens.length} {notaItens.length === 1 ? 'item' : 'itens'}</span></div>
            <div className="entrada-nota-table-wrap">
              <table className="page-content-table entrada-nota-table">
                <thead><tr><th>#</th><th>Código</th><th>Produto</th><th>Un.</th><th className="text-end">Quantidade</th><th className="text-end">Valor unit.</th><th className="text-end">Total</th><th>Ações</th></tr></thead>
                <tbody>
                  {notaItens.length === 0 ? (
                    <tr><td colSpan="8" className="entrada-nota-empty">Nenhum item adicionado. Pesquise um produto acima para começar.</td></tr>
                  ) : notaItens.map((item, index) => (
                    <tr key={`${item.produtoId}-${index}`} className={indiceEdicao === index ? 'selected' : ''}>
                      <td>{index + 1}</td><td>{item.produtoId}</td><td>{item.xProd || item.produto?.descricao}</td><td>{item.unCom}</td>
                      <td className="text-end">{formatNumeric(item.qtdCom, 2)}</td><td className="text-end">{formatCurrency(item.vUnCom)}</td><td className="text-end fw-semibold">{formatCurrency(item.vProd)}</td>
                      <td><div className="entrada-nota-row-actions"><button type="button" className="btn btn-sm btn-outline-primary" onClick={() => editarItem(index)} title="Editar item"><FaEdit /><span>Editar</span></button><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => excluirItem(index)} title="Excluir item"><FaTrashAlt /><span>Excluir</span></button></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </PageContent>

        <PageFooter>
          <div className="entrada-nota-resumo"><span>Subtotal <strong>{formatCurrency(subtotal)}</strong></span><span>Ajustes <strong>{formatCurrency(-parseNumeric(nota.vDesc) + parseNumeric(nota.vFrete) + parseNumeric(nota.vOutro))}</strong></span><span className="entrada-nota-total">Total <strong>{formatCurrency(totalNota)}</strong></span></div>
          <div className="botoes-footer">{modoEdicao && <button type="button" className="btn btn-danger btn-footer" disabled={isSaving || isDeleting} onClick={abrirExclusao}><FaTrashAlt /> Excluir entrada</button>}<button type="button" className="btn btn-outline-danger btn-footer" disabled={isDeleting} onClick={confirmarLimpeza}>{modoEdicao ? <FaTimes /> : <FaTrashAlt />} {modoEdicao ? 'Cancelar edição' : 'Limpar'}</button><button type="button" className="btn btn-primary btn-footer" disabled={isSaving || isDeleting || !naturezasEntrada.length} onClick={concluirEntrada}><FaCheck /> {isSaving ? 'Salvando...' : modoEdicao ? 'Salvar alterações' : 'Concluir entrada'}</button></div>
        </PageFooter>

        <ProdutoCad show={showCadastroProduto} onHide={() => setShowCadastroProduto(false)} onSaveProduto={(produto) => { selecionarProduto(produto); setShowCadastroProduto(false); }} />
        <ExcluirEntradaModal
          show={showExclusao}
          notaId={notaId}
          justificativa={justificativaExclusao}
          onChangeJustificativa={setJustificativaExclusao}
          onClose={() => !isDeleting && setShowExclusao(false)}
          onConfirm={confirmarExclusao}
          excluindo={isDeleting}
          error={erroExclusao}
          possuiAlteracoes={possuiAlteracoes}
        />
      </div>
    </PageContainer>
  );
};

export default EntradaNotaPage;
